import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSocket } from '../contexts/SocketContext';
import { SITE_URL } from '../config/legal';
import { isNativePlatform } from '../utils/platform';

const inviteFromLink = () => {
  const code = new URLSearchParams(window.location.hash.slice(1)).get('friends') || '';
  return /^[A-F0-9]{12}$/.test(code) ? code : '';
};

/**
 * The link a friend can open. Inside the store app the page origin is
 * capacitor://localhost, which nobody else can open, so the public site is used.
 */
export function friendsInviteLink(code: string, native = isNativePlatform(), origin = window.location.origin): string {
  const link = new URL('/', native ? SITE_URL : origin);
  link.hash = `friends=${code}`;
  return link.toString();
}

/** <dialog>.showModal needs WebKit 15.4+; older engines get a plain open dialog. */
function openDialog(dialog: HTMLDialogElement): boolean {
  if (typeof dialog.showModal === 'function') {
    try { dialog.showModal(); return true; } catch { /* fall back below */ }
  }
  dialog.setAttribute('open', '');
  return false;
}

function closeDialog(dialog: HTMLDialogElement) {
  if (typeof dialog.close === 'function') dialog.close();
  else dialog.removeAttribute('open');
}

export default function FriendsControls({ onSolo, renderTrigger }: { onSolo: () => void; renderTrigger?: (open: () => void) => React.ReactNode }) {
  const { friends, friendsError, changeFriends, connectionState, devSimulation } = useSocket();
  const [open, setOpen] = useState(() => !!inviteFromLink());
  const [code, setCode] = useState(inviteFromLink);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef<HTMLDialogElement>(null);
  // Each copy button confirms only its own copy.
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const [fallbackDialog, setFallbackDialog] = useState(false);
  useEffect(() => { if (friends?.code) onSolo(); }, [friends?.code, onSolo]);
  useEffect(() => { setCopied(null); }, [friends?.code]);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog) return;
    const modal = openDialog(dialog);
    setFallbackDialog(!modal);
    // Without a modal dialog there is no built-in Escape-to-cancel.
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    if (!modal) document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      closeDialog(dialog);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);
  const copy = async (what: 'code' | 'link', text: string, failure: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(what); }
    catch { setError(failure); }
  };
  const disabled = busy || connectionState !== 'online' || devSimulation?.active;
  const change = async (action: 'create' | 'join' | 'leave' | 'cancel') => {
    setBusy(true); setError('');
    try {
      await changeFriends(action, code);
      // Friends overrides the Solo view. Leaving returns to Solo, never public.
      if (action === 'leave') onSolo();
      setCode(''); setCopied(null);
      if (inviteFromLink()) window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not change group.'); }
    finally { setBusy(false); }
  };
  return <>
    {!friends?.code && renderTrigger ? renderTrigger(() => setOpen(true)) : <div className="friends-controls"><button className="btn-quiet" aria-haspopup="dialog" onClick={() => setOpen(true)}>
      {friends?.code ? `Friends · ${friends.members}/20` : 'Play with friends'}
    </button></div>}
    {createPortal(<dialog ref={dialogRef} className={`friends-dialog${fallbackDialog ? ' is-fallback' : ''}`} aria-labelledby="friends-dialog-title" onCancel={() => setOpen(false)} onClick={event => { if (event.target === event.currentTarget) setOpen(false); }}>
      <div className="friends-dialog-body">
      <div className="friends-dialog-heading"><h2 id="friends-dialog-title">{friends?.code ? 'Your friends group' : 'Play with friends'}</h2><button className="btn-quiet" autoFocus onClick={() => setOpen(false)}>Close</button></div>
      <div className="friends-panel">
      <p className="friends-hint">Same run, just your group. Wagers stay private from other players.</p>
      {friends?.code ? <>
        <label className="control-label" htmlFor="friends-invite">Invite code</label>
        <input id="friends-invite" value={friends.code} readOnly onFocus={e => e.target.select()} />
        <div className="friends-actions">
          <button className="btn-quiet" onClick={() => void copy('code', friends.code!, 'Select the invite code above and copy it.')}>
            {copied === 'code' ? 'Copied' : 'Copy code'}
          </button>
          <button className="btn-quiet" onClick={() => void copy('link', friendsInviteLink(friends.code!), 'Copy the invite code above instead.')}>
            {copied === 'link' ? 'Link copied' : 'Copy invite link'}
          </button>
          <button className="btn-quiet" disabled={disabled || !!friends.pending} onClick={() => void change('leave')}>Leave for Solo</button>
        </div>
        <p className="friends-hint">Share only with people you want to join. Codes expire when everyone leaves or the server restarts.</p>
      </> : <>
        <button className="btn-settings" disabled={disabled || !!friends?.pending} onClick={() => void change('create')}>Create group</button>
        <label className="control-label" htmlFor="friends-join">Have an invite?</label>
        <input id="friends-join" value={code} maxLength={12} autoComplete="off" spellCheck={false} placeholder="12-character code" onChange={e => setCode(e.target.value.toUpperCase())} />
        <button className="btn-quiet" disabled={disabled || !!friends?.pending || !/^[A-F0-9]{12}$/.test(code.trim())} onClick={() => void change('join')}>Join group</button>
      </>}
      {friends?.pending && <div role="status"><p>{friends.pending === 'leave' ? 'Leaving' : 'Joining'} next round. Your current wager stays in its original mode.</p><button className="btn-quiet" disabled={disabled} onClick={() => void change('cancel')}>Cancel change</button></div>}
      {(error || friendsError) && <p className="control-error" role="alert">{error || friendsError}</p>}
      </div>
      </div>
    </dialog>, document.body)}
  </>;
}
